import React, { useCallback, useContext, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import DeleteIcon from 'mdi-react/DeleteOutlineIcon';
import DashboardActionCard from '../cards/dashboard-action-card.component';
import ResendEmailModal from './resend-email-modal.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import notify from '../../../core/components/notify/notify.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import type User from '../../authentication/models/user';
import { deleteAdminById, resendCredentialsByEmail } from '../../authentication/user/user.service';
import ConfirmationModal from '../../offices/modals/confirmation-modal.component';
import * as styles from './modal.styles';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface Props {
  readonly showModal: boolean;
  readonly user: User;
  readonly handleClose: () => void;
  readonly handleDidDeleteUser: (user: User) => void;
}

const DashboardUserModal = ({ showModal, user, handleClose, handleDidDeleteUser }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResendEmailModal, setShowResendEmailModal] = useState(false);
  const deleteUser = useCallback(async (): Promise<void> => {
    if (user?.id != null) {
      const success = await deleteAdminById(user.id, keycloak);
      if (success) {
        notify(t('administrator-delete-success-message'), true);
        handleDidDeleteUser(user);
        handleClose();
      } else {
        notify(t('administrator-delete-error-message'), false);
      }
    } else {
      notify(t('administrator-delete-error-message'), false);
    }
    setShowDeleteModal(false);
  }, [user, handleDidDeleteUser, handleClose, keycloak, t]);

  const resendEmail = useCallback(async (): Promise<void> => {
    if (user?.email != null) {
      const success = await resendCredentialsByEmail(user.email, keycloak);
      if (success) {
        notify(t('email-success-message'), true);
      } else {
        notify(t('email-error-message'), false);
      }
    } else {
      notify(t('email-error-message'), false);
    }
    setShowResendEmailModal(false);
  }, [user, keycloak, t]);

  return (
    <>
      <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
        <Column crossAxisAlignment={CrossAxisAlignment.Stretch} itemSpacing={12}>
          <span className={style(styles.columnHeader)}>{user.firstName}</span>
          <DashboardActionCard
            text={t('resend-login-details')}
            onClick={(): void => {
              setShowResendEmailModal(true);
            }}
          />
          <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
            <span className={style(styles.modalTitle)}>{t('email')}</span>
            <span className={style(styles.modalBody)}>{user.email}</span>
          </Row>
          {keycloak.tokenParsed?.email !== user.email && (
            <Row
              className={style(styles.deleteButton)}
              mainAxisAlignment={MainAxisAlignment.Stretch}
              crossAxisAlignment={CrossAxisAlignment.Center}
              onClick={(): void => {
                setShowDeleteModal(true);
              }}
            >
              <span>{t('delete-user')}</span>
              <DeleteIcon className={style(styles.deleteIcon)} />
            </Row>
          )}

          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('close')}
            </PrimaryButton>
          </div>
        </Column>
      </Modal>
      <ConfirmationModal
        showModal={showDeleteModal}
        itemName={user.email}
        handleAccept={(): void => {
          deleteUser();
        }}
        handleClose={(): void => {
          setShowDeleteModal(false);
        }}
      />
      <ResendEmailModal
        showModal={showResendEmailModal}
        handleAccept={(): void => {
          resendEmail();
        }}
        handleClose={(): void => {
          setShowResendEmailModal(false);
        }}
      />
    </>
  );
};

export default DashboardUserModal;
